import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Handshake } from "lucide-react";

const ACCENT = "#00B0FF";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder:text-white/30 outline-none transition-colors duration-300 focus:border-[#00B0FF] focus:ring-2 focus:ring-[#00B0FF]/25";

export const LandownerPortal = () => {
  const [form, setForm] = useState({
    owner_name: "",
    email: "",
    phone: "",
    suburb: "",
    lot_size: "",
    zoning: "",
    intent: "jv",
    details: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.owner_name || !form.email || !form.suburb) {
      toast.error("Please add your name, email and site suburb.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads/site`, form);
      toast.success("Site received — our acquisitions team will be in touch within 2 business days.");
      setForm({ owner_name: "", email: "", phone: "", suburb: "", lot_size: "", zoning: "", intent: "jv", details: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="have-a-site" className="px-6 sm:px-12 lg:px-20 py-28 lg:py-36 border-t border-white/10" data-testid="landowner-section">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-display text-[11px] font-bold tracking-[0.35em] text-[#00B0FF] mb-4 flex items-center gap-3">
            <MapPin size={14} /> CHAPTER 03 — SITE ACQUISITIONS &amp; JV
          </p>
          <h2 className="font-display font-extrabold tracking-tighter text-4xl sm:text-5xl text-white leading-[1.02]">
            Have a site?{" "}
            <em className="font-serif-lux italic font-semibold" style={{ color: ACCENT }}>
              Let's unlock it together.
            </em>
          </h2>
          <p className="mt-6 text-sm sm:text-base text-white/60 leading-relaxed max-w-lg">
            Sitting on undeveloped or empty land anywhere in NSW? We buy sites outright or
            co-develop through Joint Venture partnerships — you keep skin in the game and share the
            upside, we handle approvals, build and delivery.
          </p>
          <div className="mt-10 space-y-5">
            {[
              ["Outright Purchase", "Clean, fast settlement with premium valuations for corridor sites."],
              ["Joint Venture", "Partner with JAI HOMES — land equity in, developed asset out."],
              ["Feasibility First", "Free, no-obligation yield and planning assessment on your block."],
            ].map(([t, d]) => (
              <div key={t} className="flex gap-4">
                <Handshake size={18} className="shrink-0 mt-0.5" style={{ color: ACCENT }} />
                <div>
                  <p className="font-display text-sm font-bold text-white">{t}</p>
                  <p className="text-xs text-white/50 mt-1 leading-relaxed">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="glass rounded-2xl border border-white/10 p-8"
          style={{ boxShadow: "0 0 60px rgba(0,176,255,0.07)" }}
          data-testid="landowner-form"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input className={inputCls} placeholder="Full name *" value={form.owner_name} onChange={set("owner_name")} data-testid="site-owner-name" />
            <input className={inputCls} type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="site-email" />
            <input className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="site-phone" />
            <input className={inputCls} placeholder="Site suburb / LGA *" value={form.suburb} onChange={set("suburb")} data-testid="site-suburb" />
            <input className={inputCls} placeholder="Lot size (e.g. 1,200 sqm)" value={form.lot_size} onChange={set("lot_size")} data-testid="site-lot-size" />
            <input className={inputCls} placeholder="Zoning (e.g. R2, B4)" value={form.zoning} onChange={set("zoning")} data-testid="site-zoning" />
          </div>

          <div className="mt-5">
            <p className="font-display text-xs font-bold tracking-[0.2em] text-white/50 mb-3">I'M LOOKING TO</p>
            <div className="flex gap-2">
              {[
                ["sell", "Sell outright"],
                ["jv", "Joint Venture"],
                ["unsure", "Not sure yet"],
              ].map(([val, label]) => (
                <button
                  type="button"
                  key={val}
                  data-testid={`site-intent-${val}`}
                  onClick={() => setForm({ ...form, intent: val })}
                  className={`flex-1 rounded-full px-3 py-2.5 font-display text-xs font-semibold border transition-all duration-300 ${
                    form.intent === val
                      ? "border-[#00B0FF] bg-[#00B0FF]/15 text-white shadow-[0_0_18px_rgba(0,176,255,0.3)]"
                      : "border-white/10 text-white/50 hover:border-white/30"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <textarea
            className={`${inputCls} mt-5 min-h-[96px] resize-none`}
            placeholder="Tell us about the site — access, services, current structures, timeline..."
            value={form.details}
            onChange={set("details")}
            data-testid="site-details"
          />

          <button
            type="submit"
            disabled={submitting}
            data-testid="site-submit-btn"
            className="mt-6 w-full rounded-full py-3.5 font-display text-sm font-bold text-[#0A0E17] bg-[#00B0FF] hover:shadow-[0_0_28px_rgba(0,176,255,0.5)] hover:scale-[1.01] transition-all duration-300 disabled:opacity-50"
          >
            {submitting ? "Sending..." : "Submit My Site"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
